import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Globe, FolderOpen, Users, UserPlus,
  Briefcase, DollarSign, FileText, Calendar, Settings,
  MessageSquare, Clock, Bell, ChevronDown, LogOut,
  Zap, Menu, X, Shield, CheckSquare, ThumbsUp
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useApp } from '../../context/AppContext';
import { fetchPlatformSettings, applyBrandingToDocument } from '../../services/platformSettings';
import './DashboardLayout.css';

const ROLE_LABELS = {
  super_admin: 'Founder',
  admin:       'Administrator',
  hr:          'HR Manager',
  mentor:      'Mentor',
  finance:     'Finance Dept',
  employee:    'Employee',
};

const ROLE_COLORS = {
  super_admin: '#C9A227',
  admin:       '#3182ce',
  hr:          '#38a169',
  mentor:      '#805ad5',
  finance:     '#d69e2e',
  employee:    '#e53e3e',
};

// Build nav per role
function getNavItems(role, can) {
  const all = [
    { to: '/admin',           label: 'Dashboard',   icon: LayoutDashboard, end: true,  roles: ['super_admin','admin','hr','mentor','finance','employee'] },
    { to: '/admin/website',   label: 'Website',     icon: Globe,           roles: ['super_admin','admin'] },
    { to: '/admin/projects',  label: 'Projects',    icon: FolderOpen,      roles: ['super_admin','admin','mentor','employee','finance','hr'] },
    { to: '/admin/tasks',     label: 'My Tasks',    icon: CheckSquare,     roles: ['employee','mentor'] },
    { to: '/admin/employees', label: 'Employees',   icon: Users,           roles: ['super_admin','admin','hr','mentor'] },
    { to: '/admin/recruitment',label:'Recruitment', icon: UserPlus,        roles: ['super_admin','hr'] },
    { to: '/admin/clients',   label: 'Clients',     icon: Briefcase,       roles: ['super_admin','admin','finance','mentor'] },
    { to: '/admin/contacts',  label: 'Contacts',    icon: Briefcase,       roles: ['super_admin','admin','hr'] },
    { to: '/admin/finance',   label: 'Finance',     icon: DollarSign,      roles: ['super_admin','finance','admin'] },
    { to: '/admin/approvals', label: 'Approvals',   icon: ThumbsUp,        roles: ['super_admin'] },
    { to: '/admin/documents', label: 'Documents',   icon: FileText,        roles: ['super_admin','finance','hr','admin'] },
    { to: '/admin/meetings',  label: 'Meetings',    icon: Calendar,        roles: ['super_admin','admin','mentor','hr','employee','finance'] },
    { to: '/admin/chat',      label: 'Team Chat',   icon: MessageSquare,   roles: ['super_admin','admin','mentor','employee','hr','finance'] },
    { to: '/admin/attendance',label: 'Attendance',  icon: Clock,           roles: ['super_admin','admin','hr','mentor','finance','employee'] },
    { to: '/admin/settings',  label: 'Settings',    icon: Settings,        roles: ['super_admin','admin'] },
  ];
  return all.filter(item => item.roles.includes(role));
}

export default function DashboardLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [notifOpen, setNotifOpen]     = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [branding, setBranding] = useState(null);
  const { user, logout, role } = useAuth();
  const { notifications, setNotifications, dataError, retryDataSync } = useApp();
  const navigate = useNavigate();
  const visibleNotifications = notifications.filter(
    (n) => !n.forRoles?.length || (role && n.forRoles.includes(role))
  );
  const unread = visibleNotifications.filter(n => !n.read).length;
  const navItems = getNavItems(role, () => {});

  useEffect(() => {
    let cancelled = false;
    fetchPlatformSettings()
      .then((s) => {
        if (!cancelled) {
          setBranding(s.branding);
          applyBrandingToDocument(s.branding);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [role]);

  useEffect(() => {
    const onUpdate = (e) => {
      const next = e?.detail?.branding;
      if (!next) return;
      setBranding(next);
      applyBrandingToDocument(next);
    };
    window.addEventListener('fortitude:platformSettingsUpdated', onUpdate);
    return () => window.removeEventListener('fortitude:platformSettingsUpdated', onUpdate);
  }, []);

  const handleLogout = () => { logout(); navigate('/login'); };
  const markAllRead  = () => setNotifications(prev => prev.map(n => ({ ...n, read: true })));

  const roleColor = ROLE_COLORS[role] || '#C9A227';
  const roleLabel = ROLE_LABELS[role] || 'User';

  return (
    <div className={`dashboard-wrapper ${sidebarOpen ? '' : 'sidebar-collapsed'}`}>
      {dataError && (
        <div
          role="alert"
          style={{
            padding: '10px 16px',
            background: 'rgba(229, 62, 62, 0.12)',
            borderBottom: '1px solid rgba(229, 62, 62, 0.35)',
            color: '#feb2b2',
            fontSize: '0.8rem',
            textAlign: 'center',
          }}
        >
          Data sync issue: {dataError}. Check Firestore rules and your network.
          <button
            type="button"
            onClick={() => retryDataSync()}
            style={{
              marginLeft: 12,
              padding: '4px 12px',
              borderRadius: 6,
              border: '1px solid rgba(255,255,255,0.35)',
              background: 'rgba(0,0,0,0.25)',
              color: 'inherit',
              fontSize: '0.78rem',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Retry sync
          </button>
        </div>
      )}
      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <div className="sidebar-logo-icon">{branding?.logoUrl ? <img src={branding.logoUrl} alt="" className="system-logo-img" /> : <Zap size={20} />}</div>
            <div className="sidebar-logo-text">
              <span className="sidebar-brand">{branding?.companyName?.toUpperCase() || 'FORTITUDE'}</span>
              <span className="sidebar-brand-sub">IT Solutions</span>
            </div>
          </div>
          <button className="sidebar-close" onClick={() => setSidebarOpen(false)}><X size={18} /></button>
        </div>

        {/* Role badge */}
        <div className="sidebar-role-badge" style={{ '--rc': roleColor }}>
          <div className="role-dot" />
          <span>{roleLabel}</span>
        </div>

        <nav className="sidebar-nav">
          {navItems.map(({ to, label, icon: Icon, end }) => (
            <NavLink key={to} to={to} end={end}
              className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
              <Icon size={18} />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="sidebar-footer">
          <div className="sidebar-user" onClick={() => setUserMenuOpen(!userMenuOpen)}>
            <div className="avatar" style={{ width:36,height:36,borderRadius:'50%',background:`linear-gradient(135deg,${roleColor},${roleColor}88)`,color:'#000',display:'flex',alignItems:'center',justifyContent:'center',fontWeight:800,fontSize:'0.75rem',flexShrink:0 }}>
              {user?.initials}
            </div>
            <div className="sidebar-user-info">
              <p className="sidebar-user-name">{user?.name?.split(' ')[0]}</p>
              <p className="sidebar-user-role" style={{ color: roleColor }}>{roleLabel}</p>
            </div>
            <ChevronDown size={14} className={`sidebar-chevron ${userMenuOpen ? 'rotated' : ''}`} />
          </div>
          {userMenuOpen && (
            <div className="sidebar-user-menu">
              <button onClick={handleLogout} className="sidebar-logout">
                <LogOut size={14} /> Sign Out
              </button>
            </div>
          )}
        </div>
      </aside>

      {/* Main */}
      <div className="dashboard-main">
        <header className="dashboard-header">
          <div className="header-left">
            {!sidebarOpen && (
              <button className="header-menu-btn" onClick={() => setSidebarOpen(true)}><Menu size={20} /></button>
            )}
            <div className="header-breadcrumb">
              <span className="header-brand-small">Fortitude IT · {roleLabel}</span>
            </div>
          </div>
          <div className="header-right">
            <div className="notif-wrapper">
              <button className={`header-icon-btn ${unread > 0 ? 'has-badge' : ''}`}
                onClick={() => { setNotifOpen(!notifOpen); setUserMenuOpen(false); }}
                data-count={unread}>
                <Bell size={18} />
              </button>
              {notifOpen && (
                <div className="notif-dropdown">
                  <div className="notif-header">
                    <span className="notif-title">Notifications</span>
                    <button className="notif-mark-read" onClick={markAllRead}>Mark all read</button>
                  </div>
                  <div className="notif-list">
                    {visibleNotifications.length === 0 && (
                      <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-dim)', fontSize: '0.8rem' }}>No notifications</div>
                    )}
                    {visibleNotifications.map(n => (
                      <div key={n.id} className={`notif-item ${n.read ? 'read' : 'unread'}`}>
                        <div className={`notif-dot type-${n.type}`} />
                        <div className="notif-content">
                          <p className="notif-text">{n.text}</p>
                          <span className="notif-time">{n.time}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="header-user">
              <div className="avatar" style={{ width:32,height:32,borderRadius:'50%',background:`linear-gradient(135deg,${roleColor},${roleColor}88)`,color:'#000',display:'flex',alignItems:'center',justifyContent:'center',fontWeight:800,fontSize:'0.7rem',flexShrink:0 }}>
                {user?.initials}
              </div>
              <span className="header-user-name">{user?.name?.split(' ')[0]}</span>
            </div>
          </div>
        </header>
        <main className="dashboard-content">
          <Outlet />
        </main>
      </div>
      {sidebarOpen && <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />}
    </div>
  );
}
